import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from "../../../utils/GetCookie";
import authNavigate from "../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../utils/popup/SuccessPopup";

const CreateLiteratureSearchModal = ({ onClose, onSuccess, onSwitchToPubMed, defaultSourceType = "FDA" }) => {
    const navigate = useNavigate();

    const [magicPrompt, setMagicPrompt] = useState("");
    const [generatedQuery, setGeneratedQuery] = useState("");

    const [isGenerating, setIsGenerating] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    const [sourceType, setSourceType] = useState(defaultSourceType);
    const [fdaQuery, setFdaQuery] = useState("");
    const [ctQuery, setCtQuery] = useState("");
    const [emaQuery, setEmaQuery] = useState("");
    const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false);
    const sourceDropdownRef = useRef(null);

    const API_BASE_URL = "/pceapi/slr/discovery";

    const headers = useMemo(() => ({
        Authorization: GetCookie("access_token"),
    }), []);

    const isProcessing = isGenerating || isCreating;

    const generateQuery = async () => {
        if (!magicPrompt.trim() || isGenerating) return;

        const isCT = sourceType === "CLINICAL_TRIAL";
        const endpoint = isCT
            ? `${API_BASE_URL}/generate-ct-query`
            : `${API_BASE_URL}/generate-query`;

        setIsGenerating(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Generating query...");
        setPopupErrorMessage("");

        try {
            const res = await axios.post(
                endpoint,
                { natural_language_query: magicPrompt.trim() },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                if (isCT) {
                    setCtQuery(res.data.data?.ct_query || "");
                } else {
                    setGeneratedQuery(res.data.data?.pubmed_query || "");
                }
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to generate query");
            }
        } catch (error) {
            if (error.response?.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            const errorMsg = error.response?.data?.message
                || error?.message
                || "Error generating query";
            setPopupErrorMessage(errorMsg);
        } finally {
            setIsGenerating(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && magicPrompt.trim() && !isGenerating) {
            generateQuery();
        }
    };

    useEffect(() => {
        const handler = (e) => {
            if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target)) {
                setIsSourceDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const buildEmaSearchQuery = (query) => {
        const trimmedQuery = query.trim();
        return `${trimmedQuery} category:Human`;
    };

    const handleCreateSearch = async () => {
        const activeQuery = sourceType === "FDA" ? fdaQuery : sourceType === "CLINICAL_TRIAL" ? ctQuery : sourceType === "EMA" ? buildEmaSearchQuery(emaQuery) : generatedQuery;
        if (!activeQuery.trim() || isCreating) return;

        setIsCreating(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Creating search...");
        setPopupErrorMessage("");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/create`,
                {
                    search_query: activeQuery.trim(),
                    source_type: sourceType,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                setPopupSuccessMessage("Literature search created successfully");
                const payload = res.data.data || res.data;
                const createdSearch = {
                    id: payload.id || payload.discovery_id,
                    query: activeQuery.trim(),
                    source_type: sourceType,
                };
                setTimeout(() => {
                    onSuccess?.(createdSearch);
                }, 1000);
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to create search");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            const errorMsg = err.response?.data?.message
                || err?.message
                || "Something went wrong";
            setPopupErrorMessage(errorMsg);
        } finally {
            setIsCreating(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    return (
        <>
            <div className="ev-card-4 position-absolute z-1 create-search-dropdown p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <p className="ev-reg-p-dark ev-bold-reg mb-0">
                            {sourceType === "FDA" ? "FDA Drug Search" : sourceType === "CLINICAL_TRIAL" ? "Clinical Trials Search" : sourceType === "EMA" ? "EMA Medicine Search" : "Generate Query using AI"}{" "}
                            <i className="gradient-font6 bi bi-stars"></i>
                        </p>
                        <div className="position-relative" ref={sourceDropdownRef}>
                            <button
                                type="button"
                                className="ev-card-1 ev-btn-xsmall ev-radius-xsmall d-flex align-items-center gap-2 ev-med-p-blue border"
                                onClick={() => setIsSourceDropdownOpen((prev) => !prev)}
                                disabled={isProcessing}
                            >
                                <span>
                                    {sourceType === "FDA" ? "FDA" : sourceType === "CLINICAL_TRIAL" ? "Clinical Trials" : sourceType === "EMA" ? "EMA" : "PubMed"}
                                </span>
                                <i className={`bi ${isSourceDropdownOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                            </button>

                            {isSourceDropdownOpen && (
                                <div className="ev-card-8 ev-radius-xsmall position-absolute top-100 start-0 mt-1 p-2 z-3" style={{ minWidth: "140px" }}>
                                    {[{ id: "PUBMED", label: "PubMed" }, { id: "FDA", label: "FDA" }, { id: "CLINICAL_TRIAL", label: "Clinical Trials" }, { id: "EMA", label: "EMA" }].map((opt) => (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            className={`w-100 text-start ev-radius-xsmall ev-btn-small mb-1 ${sourceType === opt.id ? "ev-btn-blue-fill-round" : "ev-btn-light-fill ev-reg-p-blue"}`}
                                            onClick={() => {
                                                setIsSourceDropdownOpen(false);
                                                if (opt.id === "PUBMED") {
                                                    onSwitchToPubMed?.();
                                                    return;
                                                }
                                                setSourceType(opt.id);
                                                setMagicPrompt("");
                                                setGeneratedQuery("");
                                                setFdaQuery("");
                                                setCtQuery("");
                                                setEmaQuery("");
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        type="button"
                        className="ev-card-3 ev-padding-xsmall2 border-0"
                        onClick={onClose}
                        disabled={isProcessing}
                    >
                        <i className="bi bi-x ev-med-p-dark ev-fs-18"></i>
                    </button>
                </div>

                {sourceType === "CLINICAL_TRIAL" ? (
                    <>
                        <div className="ev-card-6 mb-3 p-2 ev-radius-xsmall">
                            <div className="form-group mb-2">
                                <textarea
                                    className="formField3 bg-transparent"
                                    rows={2}
                                    placeholder="Describe your clinical trial search in plain language..."
                                    value={magicPrompt}
                                    onChange={(e) => setMagicPrompt(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={isProcessing}
                                />
                            </div>
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="ev-btn-gradient-fill-round ev-radius-xsmall ev-bold-reg ev-btn-small"
                                    onClick={generateQuery}
                                    disabled={!magicPrompt.trim() || isProcessing}
                                >
                                    {isGenerating ? (
                                        <><i className="bi bi-hourglass-split me-1"></i> Generating...</>
                                    ) : (
                                        <><i className="bi bi-play-fill"></i> Generate</>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="mb-2 text-start">
                            <label className="ev-small-p-dark ev-bold-reg mb-1">
                                Add your ClinicalTrials Search Query <span className="text-danger">*</span>
                            </label>
                            <textarea
                                className="formField3 ev-message-card2 ev-radius-xsmall w-100"
                                rows={4}
                                value={ctQuery}
                                onChange={(e) => setCtQuery(e.target.value)}
                                placeholder="e.g. Condition: lung cancer, Intervention: pembrolizumab, Location: USA"
                                disabled={isProcessing}
                            />
                        </div>

                        <div className="text-end">
                            <button
                                type="button"
                                className="ev-btn-blue-fill-round ev-btn-reg"
                                disabled={!ctQuery.trim() || isProcessing}
                                onClick={handleCreateSearch}
                            >
                                {isCreating ? "Creating..." : "Create Search"}
                            </button>
                        </div>
                    </>   
                ) : sourceType === "EMA" ? (
                    <>
                        <div className="mb-2 text-start">
                            <label className="ev-small-p-dark ev-bold-reg mb-1">
                                Search by name or active substance <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="formField3 ev-message-card2 ev-radius-xsmall w-100"
                                value={emaQuery}
                                onChange={(e) => setEmaQuery(e.target.value)}
                                placeholder="e.g. semaglutide"
                                disabled={isProcessing}
                            />
                            <p className="ev-small-p-dark mt-1 mb-0">
                                <i className="bi bi-info-circle me-1"></i>
                                Enter a brand name, generic name or active substance. Medicines for human use authorised in the EU will be retrieved.
                            </p>
                        </div>

                        <div className="text-end">
                            <button
                                type="button"
                                className="ev-btn-blue-fill-round ev-btn-reg"
                                disabled={!emaQuery.trim() || isProcessing}
                                onClick={handleCreateSearch}
                            >
                                {isCreating ? "Creating..." : "Create Search"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="mb-2 text-start">
                            <label className="ev-small-p-dark ev-bold-reg mb-1">
                                Drug / Substance Name <span className="text-danger">*</span>
                            </label>
                            <textarea
                                className="formField3 ev-message-card2 ev-radius-xsmall w-100"
                                rows={4}
                                value={fdaQuery}
                                onChange={(e) => setFdaQuery(e.target.value)}
                                placeholder="e.g. Ozempic, semaglutide, metformin..."
                                disabled={isProcessing}
                            />
                            <p className="ev-small-p-dark mt-1 mb-0">
                                <i className="bi bi-info-circle me-1"></i>
                                Enter a brand name or generic name. FDA drug applications will be retrieved.
                            </p>
                        </div>

                        <div className="text-end">
                            <button
                                type="button"
                                className="ev-btn-blue-fill-round ev-btn-reg"
                                disabled={!fdaQuery.trim() || isProcessing}
                                onClick={handleCreateSearch}
                            >
                                {isCreating ? "Creating..." : "Create Search"}
                            </button>
                        </div>
                    </>
                )}
            </div>

            {isProcessingPopupVisible &&
                ReactDOM.createPortal(
                    <ProcessingPopup message={processingMessage || "Processing..."} />,
                    document.body
                )
            }

            {popupErrorMessage &&
                ReactDOM.createPortal(
                    <ErrorPopup
                        message={popupErrorMessage}
                        onClose={() => setPopupErrorMessage("")}
                    />,
                    document.body
                )
            }

            {popupSuccessMessage &&
                ReactDOM.createPortal(
                    <SuccessPopup
                        message={popupSuccessMessage}
                        onClose={() => setPopupSuccessMessage("")}
                    />,
                    document.body
                )
            }
        </>
    );
};

export default CreateLiteratureSearchModal;